/**
 * Login / signup gate. Blocks the app until a session exists and the account
 * is approved. Name + PIN, driver-friendly: big touch targets, numeric PIN pad
 * behaviour via inputmode, no typing gymnastics at the wheel.
 */

import * as api from './api';
import type { User } from './api';
import { t } from '../core/i18n';

export class LoginView {
  readonly el: HTMLElement;
  private mode: 'login' | 'signup' = 'login';
  private busy = false;

  constructor(
    parent: HTMLElement,
    private onAuthed: (user: User) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'login';
    parent.appendChild(this.el);
    this.render();
  }

  destroy(): void {
    this.el.remove();
  }

  private render(error = '', notice = ''): void {
    const signup = this.mode === 'signup';
    this.el.innerHTML = `
      <div class="login__card">
        <div class="login__brand">
          <svg viewBox="0 0 48 48" class="login__logo"><path d="M24 4 L38 40 L24 32 L10 40 Z" fill="currentColor"/></svg>
          <h1>Wayfarer</h1>
          <p>${signup ? t('login.request') : t('login.signin')}</p>
        </div>
        ${error ? `<div class="login__error">${escapeHtml(error)}</div>` : ''}
        ${notice ? `<div class="login__notice">${escapeHtml(notice)}</div>` : ''}
        <form class="login__form">
          <label>${t('login.name')}
            <input name="name" type="text" autocomplete="username" autocapitalize="words"
                   required minlength="2" maxlength="40" placeholder="${t('login.name.ph')}" />
          </label>
          <label>${t('login.pin')}
            <input name="pin" type="password" inputmode="numeric" autocomplete="${signup ? 'new-password' : 'current-password'}"
                   required minlength="4" maxlength="10" pattern="\\d{4,10}" placeholder="${signup ? t('login.pin.new.ph') : t('login.pin.ph')}" />
          </label>
          <button type="submit" class="btn btn--primary btn--big" ${this.busy ? 'disabled' : ''}>
            ${this.busy ? t('login.wait') : signup ? t('login.request') : t('login.submit')}
          </button>
        </form>
        <button class="login__switch" type="button">
          ${signup ? t('login.toLogin') : t('login.toSignup')}
        </button>
      </div>`;

    this.el.querySelector('form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.submit(new FormData(e.target as HTMLFormElement));
    });
    this.el.querySelector('.login__switch')!.addEventListener('click', () => {
      this.mode = this.mode === 'login' ? 'signup' : 'login';
      this.render();
    });
  }

  private async submit(data: FormData): Promise<void> {
    if (this.busy) return;
    const name = String(data.get('name') ?? '').trim();
    const pin = String(data.get('pin') ?? '');
    this.busy = true;
    this.render();

    try {
      if (this.mode === 'signup') {
        await api.signup(name, pin);
        this.busy = false;
        this.mode = 'login';
        this.render('', t('login.requested'));
        return;
      }
      const { user } = await api.login(name, pin);
      if (user.status === 'pending') {
        this.busy = false;
        this.render('', t('login.pending'));
        return;
      }
      this.onAuthed(user);
    } catch (err) {
      this.busy = false;
      this.render(err instanceof Error ? err.message : t('login.error'));
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
